import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * LLMSettingsModal - Modal-based LLM Provider Configuration
 * Replaces the old SettingsTab with a cleaner modal interface
 */
function LLMSettingsModal({
  isOpen,
  onClose,
  // Provider Settings
  llmProvider,
  setLlmProvider,
  apiKey,
  setApiKey,
  baseUrl,
  setBaseUrl,
  model,
  setModel,
  temperature,
  setTemperature,
  maxTokens,
  setMaxTokens,
  // Structured Outputs
  useStructuredOutputs,
  setUseStructuredOutputs,
  // System Prompt
  systemPrompt,
  setSystemPrompt,
  getDefaultSystemPrompt,
  // Actions
  onSaveSettings
}) {
  // Local state for editing (only save on confirm)
  const [localSettings, setLocalSettings] = useState({
    llmProvider,
    apiKey,
    baseUrl,
    model,
    temperature,
    maxTokens,
    useStructuredOutputs,
    systemPrompt
  });
  
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  // Sync local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSettings({
        llmProvider,
        apiKey,
        baseUrl,
        model,
        temperature,
        maxTokens,
        useStructuredOutputs: useStructuredOutputs || false,
        systemPrompt
      });
    }
  }, [isOpen, llmProvider, apiKey, baseUrl, model, temperature, maxTokens, useStructuredOutputs, systemPrompt]);

  // Handle provider change with automatic model/URL updates
  const handleProviderChange = (newProvider) => {
    let newModel = localSettings.model;
    let newBaseUrl = localSettings.baseUrl;
    
    if (newProvider === 'openai') {
      newModel = 'gpt-4o';
      newBaseUrl = '';
    } else if (newProvider === 'anthropic') {
      newModel = 'claude-3-5-sonnet-20241022';
      newBaseUrl = '';
    } else if (newProvider === 'groq') {
      newModel = 'llama-3.3-70b-versatile';
      newBaseUrl = 'https://api.groq.com/openai/v1/chat/completions';
    } else if (newProvider === 'openrouter') {
      newModel = 'meta-llama/llama-3.1-70b-instruct';
      newBaseUrl = 'https://openrouter.ai/api/v1/chat/completions';
    }
    
    setLocalSettings(prev => ({
      ...prev,
      llmProvider: newProvider,
      model: newModel,
      baseUrl: newBaseUrl
    }));
  };

  // Save and close
  const handleSave = () => {
    setLlmProvider(localSettings.llmProvider);
    setApiKey(localSettings.apiKey);
    setBaseUrl(localSettings.baseUrl);
    setModel(localSettings.model);
    setTemperature(localSettings.temperature);
    setMaxTokens(localSettings.maxTokens);
    if (setUseStructuredOutputs) {
      setUseStructuredOutputs(localSettings.useStructuredOutputs);
    }
    setSystemPrompt(localSettings.systemPrompt);
    
    if (onSaveSettings) {
      onSaveSettings();
    }
    
    onClose();
  };

  // Check if structured outputs are supported
  const supportsStructuredOutputs = ['openai'].includes(localSettings.llmProvider);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Einstellungen
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            
            {/* Provider Selection */}
            <div>
              <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
                🔌 Provider
              </label>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { id: 'openai', label: 'OpenAI', icon: '🤖' },
                  { id: 'anthropic', label: 'Anthropic', icon: '🧠' },
                  { id: 'groq', label: 'Groq', icon: '⚡' },
                  { id: 'openrouter', label: 'OpenRouter', icon: '🌐' },
                  { id: 'custom', label: 'Custom', icon: '🔧' }
                ].map(provider => (
                  <button
                    key={provider.id}
                    onClick={() => handleProviderChange(provider.id)}
                    className={`
                      p-3 rounded-lg border-2 transition-all text-center
                      ${localSettings.llmProvider === provider.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }
                    `}
                  >
                    <div className="text-xl mb-1">{provider.icon}</div>
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {provider.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Provider-specific info */}
            {localSettings.llmProvider === 'groq' && (
              <div className="bg-success-50 dark:bg-success-900/30 border border-success-300 dark:border-success-700 rounded-lg p-3">
                <p className="text-sm text-success-800 dark:text-success-300">
                  ⚡ <strong>Groq</strong> - Kostenlos & sehr schnell! 
                  <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                    API Key holen →
                  </a>
                </p>
              </div>
            )}
            
            {localSettings.llmProvider === 'openrouter' && (
              <div className="bg-primary-50 dark:bg-primary-900/30 border border-primary-300 dark:border-primary-700 rounded-lg p-3">
                <p className="text-sm text-primary-800 dark:text-primary-300">
                  🌐 <strong>OpenRouter</strong> - Zugang zu vielen Modellen (günstig).
                  <a href="https://openrouter.ai" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                    Registrieren →
                  </a>
                </p>
              </div>
            )}

            {/* API Key & Model */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  🔑 API Key
                </label>
                <input
                  type="password"
                  value={localSettings.apiKey}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, apiKey: e.target.value }))}
                  placeholder={
                    localSettings.llmProvider === 'openai' ? 'sk-...' :
                    localSettings.llmProvider === 'anthropic' ? 'sk-ant-...' :
                    localSettings.llmProvider === 'groq' ? 'gsk_...' :
                    localSettings.llmProvider === 'openrouter' ? 'sk-or-...' :
                    'Optional'
                  }
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  🤖 Model
                </label>
                <input
                  type="text"
                  value={localSettings.model}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="gpt-4o"
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Base URL (for custom endpoints) */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                🌐 Base URL <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <input
                type="url"
                value={localSettings.baseUrl}
                onChange={(e) => setLocalSettings(prev => ({ ...prev, baseUrl: e.target.value }))}
                placeholder={
                  localSettings.llmProvider === 'groq' ? 'https://api.groq.com/openai/v1/chat/completions' :
                  localSettings.llmProvider === 'openrouter' ? 'https://openrouter.ai/api/v1/chat/completions' :
                  'https://api.example.com/v1/chat/completions'
                }
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leer lassen für Standard-Endpoint des Providers
              </p>
            </div>

            {/* Temperature & Max Tokens */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  🎚️ Temperature: <span className="font-mono">{localSettings.temperature}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={localSettings.temperature}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                  className="w-full accent-primary-600"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>Präzise</span>
                  <span>Kreativ</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  📊 Max Tokens
                </label>
                <input
                  type="number"
                  min="100"
                  max="32000"
                  step="100"
                  value={localSettings.maxTokens}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, maxTokens: parseInt(e.target.value) || 4000 }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Structured Outputs Toggle */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <span>🎯</span> Structured Outputs
                    {!supportsStructuredOutputs && (
                      <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                        Nur OpenAI
                      </span>
                    )}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Garantiert valides JSON nach Schema (weniger Parsing-Fehler)
                  </p>
                </div>
                <button
                  onClick={() => setLocalSettings(prev => ({ ...prev, useStructuredOutputs: !prev.useStructuredOutputs }))}
                  disabled={!supportsStructuredOutputs}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full theme-transition
                    ${localSettings.useStructuredOutputs && supportsStructuredOutputs
                      ? 'bg-primary-600'
                      : 'bg-gray-300 dark:bg-gray-600'
                    }
                    ${!supportsStructuredOutputs && 'opacity-50 cursor-not-allowed'}
                  `}
                >
                  <span className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    ${localSettings.useStructuredOutputs ? 'translate-x-6' : 'translate-x-1'}
                  `} />
                </button>
              </div>
            </div>

            {/* System Prompt (Collapsible) */}
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowSystemPrompt(!showSystemPrompt)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <span className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <span>📝</span> System Prompt
                </span>
                <svg 
                  className={`w-5 h-5 text-gray-500 transition-transform ${showSystemPrompt ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showSystemPrompt && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={() => setLocalSettings(prev => ({ ...prev, systemPrompt: getDefaultSystemPrompt() }))}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Auf Default zurücksetzen
                    </button>
                  </div>
                  <textarea
                    value={localSettings.systemPrompt}
                    onChange={(e) => setLocalSettings(prev => ({ ...prev, systemPrompt: e.target.value }))}
                    rows={12}
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono text-xs"
                    placeholder="System Prompt..."
                  />
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

LLMSettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  llmProvider: PropTypes.string.isRequired,
  setLlmProvider: PropTypes.func.isRequired,
  apiKey: PropTypes.string.isRequired,
  setApiKey: PropTypes.func.isRequired,
  baseUrl: PropTypes.string.isRequired,
  setBaseUrl: PropTypes.func.isRequired,
  model: PropTypes.string.isRequired,
  setModel: PropTypes.func.isRequired,
  temperature: PropTypes.number.isRequired,
  setTemperature: PropTypes.func.isRequired,
  maxTokens: PropTypes.number.isRequired,
  setMaxTokens: PropTypes.func.isRequired,
  useStructuredOutputs: PropTypes.bool,
  setUseStructuredOutputs: PropTypes.func,
  systemPrompt: PropTypes.string.isRequired,
  setSystemPrompt: PropTypes.func.isRequired,
  getDefaultSystemPrompt: PropTypes.func.isRequired,
  onSaveSettings: PropTypes.func
};

export default LLMSettingsModal;
